import { OfferTokenSchema, buildOfferHandoffUrl } from '@rheora/enrollment-contract';

/**
 * E-SF16 — the `/offer/:token` landing route's logic. PURE: no React, no DOM, no
 * network. `OfferEntryPage.tsx` renders; every decision on the hop is decided here.
 *
 * ── WHAT THIS PAGE IS, AND WHAT IT IS NOT ──────────────────────────────────
 * It is a HAND-OFF, not a second shelf. Quotation builds the emailed link as
 * `<offerBaseUrl>/offer/<token>` — a US-Shop path, because the ingress path-routes
 * `/bff/*` to the BFF and `/` to the SPA — so the customer's click lands in the
 * SPA holding a token the SPA cannot do anything with. The token is exchanged
 * SERVER-SIDE (it is a capability against a credentialed, RLS-scoped upstream),
 * so this page's entire job is to get the browser to `GET /bff/offer/:token` and
 * let the 302 take it into the funnel.
 *
 * ── WHY A FULL NAVIGATION AND NOT A FETCH ──────────────────────────────────
 * ⚠️ THIS IS THE ONE THING THAT WOULD BREAK SUBTLY IF CHANGED. `fetch` follows a
 * 302 transparently and resolves with the TARGET's body — for a hand-off into the
 * storefront, the SPA's own `index.html`. The `Set-Cookie` would land and the
 * request would report success, but the browser would never move: the customer
 * would sit on a blank landing page while a perfectly good funnel session waited
 * for them elsewhere. A location assignment makes the redirect the BROWSER'S
 * navigation, which is what a redirect is for.
 *
 * `replace`, not `assign`: the hand-off URL must not become a back-button
 * destination. Re-entering it would mint a SECOND fresh session, discarding
 * whatever the customer had done since — and would spend another of their ten
 * hourly offer-entry allowances.
 */

/**
 * What the landing page should do with the token in its URL.
 *
 * `handoff` carries the URL rather than the token so the component never
 * assembles one, and `unusable` carries no reason — there is exactly one failure
 * outcome and the page must not be able to render a second (US-SF16.2).
 */
export type OfferEntryAction =
  | { readonly kind: 'handoff'; readonly url: string }
  | { readonly kind: 'unusable' };

/**
 * Decide the landing page's action from the raw `:token` route param.
 *
 * ── WHY THE CLIENT VALIDATES AT ALL ────────────────────────────────────────
 * Not as a control — the BFF re-validates and is the authority, and a caller can
 * skip this page entirely by requesting `/bff/offer/...` directly. It exists so a
 * plainly-unusable link (a truncated copy-paste, a mail client that mangled the
 * URL) does not spend one of the customer's ten hourly offer-entry allowances on a
 * request that cannot succeed. The shape rule is the CONTRACT's
 * `OfferTokenSchema`, imported rather than restated, so the two cannot disagree
 * about what a token looks like.
 *
 * @param token the `:token` route param, or `undefined` when the route matched
 *   without one (which react-router will not do for this pattern, but the type
 *   allows it and a `!` here would be a lie).
 */
export function decideOfferEntry(token: string | undefined): OfferEntryAction {
  const parsed = OfferTokenSchema.safeParse(token);
  if (!parsed.success) return { kind: 'unusable' };
  return { kind: 'handoff', url: buildOfferHandoffUrl(parsed.data) };
}
